import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "feature-flags-tab" }
const _hoisted_2 = { class: "status-section" }
const _hoisted_3 = { class: "status-header" }
const _hoisted_4 = { class: "features-section" }
const _hoisted_5 = { class: "section-header" }
const _hoisted_6 = { class: "count-badge" }
const _hoisted_7 = {
  key: 0,
  class: "empty-state"
}
const _hoisted_8 = {
  key: 1,
  class: "features-list"
}
const _hoisted_9 = ["onClick"]
const _hoisted_10 = ["title"]
const _hoisted_11 = { class: "feature-type" }
const _hoisted_12 = ["onClick", "title"]
const _hoisted_13 = ["onClick"]
const _hoisted_14 = {
  key: 0,
  class: "feature-value"
}
const _hoisted_15 = { class: "value-row" }
const _hoisted_16 = {
  key: 0,
  class: "value-row override"
}
const _hoisted_17 = { class: "attributes-section" }
const _hoisted_18 = { class: "attributes-content" }
const _hoisted_19 = { class: "actions-section" }
const _hoisted_20 = ["disabled"]

import { ref, computed, onMounted } from "vue";
import { GrowthBookService } from "@/services/GrowthBookService";

const OVERRIDES_KEY = "ai-assistant-gb-overrides";

interface FeatureItem {
  key: string;
  value: any;
  type: string;
  displayValue: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'FeatureFlagsTab',
  setup(__props) {

const gbService = GrowthBookService.getInstance();

const isReady = ref(false);
const features = ref<Record<string, any>>({});
const attributes = ref<Record<string, any>>({});
const overrides = ref<Record<string, any>>({});
const expandedKeys = ref<string[]>([]);

const featureCount = computed(() => Object.keys(features.value).length);

const featuresList = computed((): FeatureItem[] => {
  const items: FeatureItem[] = [];

  for (const [key, featureData] of Object.entries(features.value)) {
    // GrowthBook features have a defaultValue property
    const value = featureData?.defaultValue ?? featureData;
    const type = typeof value;
    let displayValue: string;

    if (type === "object" && value !== null) {
      displayValue = JSON.stringify(value, null, 2);
    } else {
      displayValue = String(value);
    }

    items.push({ key, value, type, displayValue });
  }

  return items.sort((a, b) => a.key.localeCompare(b.key));
});

const attributesDisplay = computed(() => {
  if (Object.keys(attributes.value).length === 0) {
    return "{}";
  }
  return JSON.stringify(attributes.value, null, 2);
});

const hasAnyOverrides = computed(() => Object.keys(overrides.value).length > 0);

const formatValue = (value: any): string => {
  if (typeof value === "object" && value !== null) {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

const loadOverrides = () => {
  try {
    const stored = localStorage.getItem(OVERRIDES_KEY);
    if (stored) {
      overrides.value = JSON.parse(stored);
    }
  } catch (err) {
    console.error("[FeatureFlagsTab] Failed to load overrides", err);
    overrides.value = {};
  }
};

const saveOverrides = () => {
  try {
    localStorage.setItem(OVERRIDES_KEY, JSON.stringify(overrides.value));
    gbService.setForcedFeatures(overrides.value);
  } catch (err) {
    console.error("[FeatureFlagsTab] Failed to save overrides", err);
  }
};

const refreshFeatures = () => {
  isReady.value = gbService.isReady();
  features.value = gbService.getAllFeatures();
  attributes.value = gbService.getAttributes();
};

const toggleExpand = (key: string) => {
  const index = expandedKeys.value.indexOf(key);
  if (index === -1) {
    expandedKeys.value.push(key);
  } else {
    expandedKeys.value.splice(index, 1);
  }
};

const toggleFeature = (key: string, currentValue: boolean) => {
  const currentOverride = overrides.value[key];
  const newValue = currentOverride !== undefined ? !currentOverride : !currentValue;

  overrides.value[key] = newValue;
  saveOverrides();
};

const hasOverride = (key: string): boolean => {
  return key in overrides.value;
};

const getOverrideValue = (key: string): any => {
  return overrides.value[key];
};

const removeOverride = (key: string) => {
  delete overrides.value[key];
  saveOverrides();
};

const clearAllOverrides = () => {
  if (confirm("Are you sure you want to clear all feature flag overrides?")) {
    overrides.value = {};
    localStorage.removeItem(OVERRIDES_KEY);
    gbService.setForcedFeatures({});
    refreshFeatures();
  }
};

onMounted(() => {
  loadOverrides();
  refreshFeatures();

  // Apply existing overrides
  if (Object.keys(overrides.value).length > 0) {
    gbService.setForcedFeatures(overrides.value);
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[0] || (_cache[0] = _createElementVNode("span", { class: "status-title" }, "GrowthBook Status", -1)),
        _createElementVNode("span", {
          class: _normalizeClass(["status-badge", { ready: isReady.value }])
        }, _toDisplayString(isReady.value ? "Ready" : "Not Ready"), 3)
      ])
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("div", _hoisted_5, [
        _cache[1] || (_cache[1] = _createElementVNode("span", { class: "section-title" }, "Features", -1)),
        _createElementVNode("span", _hoisted_6, _toDisplayString(featureCount.value), 1)
      ]),
      (featureCount.value === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [...(_cache[2] || (_cache[2] = [
            _createElementVNode("svg", {
              viewBox: "0 0 24 24",
              fill: "none",
              stroke: "currentColor",
              "stroke-width": "2",
              width: "24",
              height: "24"
            }, [
              _createElementVNode("path", { d: "M3 3v18h18" }),
              _createElementVNode("path", { d: "m19 9-5 5-4-4-3 3" })
            ], -1),
            _createElementVNode("span", null, "No features loaded", -1)
          ]))]))
        : (_openBlock(), _createElementBlock("div", _hoisted_8, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(featuresList.value, (feature) => {
              return (_openBlock(), _createElementBlock("div", {
                key: feature.key,
                class: _normalizeClass(["feature-item", { expanded: expandedKeys.value.includes(feature.key) }])
              }, [
                _createElementVNode("div", {
                  class: "feature-header",
                  onClick: ($event: any) => (toggleExpand(feature.key))
                }, [
                  _cache[5] || (_cache[5] = _createElementVNode("svg", {
                    viewBox: "0 0 24 24",
                    fill: "none",
                    stroke: "currentColor",
                    "stroke-width": "2",
                    width: "12",
                    height: "12",
                    class: "chevron"
                  }, [
                    _createElementVNode("path", { d: "m9 18 6-6-6-6" })
                  ], -1)),
                  _createElementVNode("span", {
                    class: "feature-key",
                    title: feature.key
                  }, _toDisplayString(feature.key), 9, _hoisted_10),
                  _createElementVNode("span", _hoisted_11, _toDisplayString(feature.type), 1),
                  (feature.type === 'boolean')
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 0,
                        class: _normalizeClass(["toggle-btn", { active: getOverrideValue(feature.key) ?? feature.value }]),
                        onClick: _withModifiers(($event: any) => (toggleFeature(feature.key, feature.value)), ["stop"]),
                        title: hasOverride(feature.key) ? 'Override active' : 'Click to override'
                      }, [...(_cache[3] || (_cache[3] = [
                        _createElementVNode("span", { class: "toggle-slider" }, null, -1)
                      ]))], 10, _hoisted_12))
                    : _createCommentVNode("", true),
                  (hasOverride(feature.key))
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 1,
                        class: "reset-btn",
                        onClick: _withModifiers(($event: any) => (removeOverride(feature.key)), ["stop"]),
                        title: "Remove override"
                      }, [...(_cache[4] || (_cache[4] = [
                        _createElementVNode("svg", {
                          viewBox: "0 0 24 24",
                          fill: "none",
                          stroke: "currentColor",
                          "stroke-width": "2",
                          width: "12",
                          height: "12"
                        }, [
                          _createElementVNode("path", { d: "M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" }),
                          _createElementVNode("path", { d: "M3 3v5h5" })
                        ], -1)
                      ]))], 8, _hoisted_13))
                    : _createCommentVNode("", true)
                ], 8, _hoisted_9),
                (expandedKeys.value.includes(feature.key))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                      _createElementVNode("div", _hoisted_15, [
                        _cache[6] || (_cache[6] = _createElementVNode("span", { class: "value-label" }, "Default:", -1)),
                        _createElementVNode("pre", null, _toDisplayString(feature.displayValue), 1)
                      ]),
                      (hasOverride(feature.key))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                            _cache[7] || (_cache[7] = _createElementVNode("span", { class: "value-label" }, "Override:", -1)),
                            _createElementVNode("pre", null, _toDisplayString(formatValue(getOverrideValue(feature.key))), 1)
                          ]))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ], 2))
            }), 128))
          ]))
    ]),
    _createElementVNode("div", _hoisted_17, [
      _cache[8] || (_cache[8] = _createElementVNode("div", { class: "section-header" }, [
        _createElementVNode("span", { class: "section-title" }, "Targeting Attributes")
      ], -1)),
      _createElementVNode("div", _hoisted_18, [
        _createElementVNode("pre", null, _toDisplayString(attributesDisplay.value), 1)
      ])
    ]),
    _createElementVNode("div", _hoisted_19, [
      _createElementVNode("button", {
        class: "action-btn",
        onClick: refreshFeatures
      }, [...(_cache[9] || (_cache[9] = [
        _createElementVNode("svg", {
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          "stroke-width": "2",
          width: "12",
          height: "12"
        }, [
          _createElementVNode("path", { d: "M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" }),
          _createElementVNode("path", { d: "M3 3v5h5" })
        ], -1),
        _createTextVNode(" Refresh ", -1)
      ]))]),
      _createElementVNode("button", {
        class: _normalizeClass(["action-btn danger", { disabled: !hasAnyOverrides.value }]),
        onClick: clearAllOverrides,
        disabled: !hasAnyOverrides.value
      }, [...(_cache[10] || (_cache[10] = [
        _createElementVNode("svg", {
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          "stroke-width": "2",
          width: "12",
          height: "12"
        }, [
          _createElementVNode("path", { d: "M18 6 6 18" }),
          _createElementVNode("path", { d: "m6 6 12 12" })
        ], -1),
        _createTextVNode(" Clear All Overrides ", -1)
      ]))], 10, _hoisted_20)
    ])
  ]))
}
}

})